/**
 * vehicle-finder.js
 * ---------------------------------------------------------------------
 * Cascading Make -> Model -> Body Style -> Doors filter, backed entirely
 * by Shopify's native collection.filters object (Search & Discovery).
 *
 * How it stays in sync with real inventory:
 * Every step re-fetches /collections/{make}?view=filters-api with the
 * filters chosen so far attached as query params. Shopify recalculates
 * counts for every remaining filter value server-side, so an option that
 * would return zero products comes back with count: 0 and gets disabled.
 * No product data is duplicated or hardcoded on the front end.
 */
(function () {
  'use strict';

  // Capitalizes the first letter of each word for display only — never
  // lowercases the rest, so values that are already correctly cased
  // (e.g. "SUV", "Q4") pass through unchanged. Raw tag values like
  // "range rover sport" become "Range Rover Sport".
  function capitalizeWords(str) {
    if (!str) return str;
    return str.replace(/\b\w/g, function (c) { return c.toUpperCase(); });
  }

  function VehicleFinder(root, labels) {
    this.root = root;
    this.labels = labels || {};
    this.makeSelect = root.querySelector('[data-vf-make]');
    this.modelSelect = root.querySelector('[data-vf-model]');
    this.bodySelect = root.querySelector('[data-vf-body]');
    this.doorsSelect = root.querySelector('[data-vf-doors]');
    this.searchBtn = root.querySelector('[data-vf-search]');
    this.statusEl = root.querySelector('[data-vf-status]');
    this.form = root.querySelector('[data-vf-form]');

    this.collectionUrl = null;
    this.selected = { model: null, body: null, doors: null };
    this.requestToken = 0;

    this.bindEvents();

    // Browsers auto-restore a <select>'s value on back/forward navigation
    // without firing a change event. If Make already has a value on load
    // (restored by the browser), re-run the same cascade a manual
    // selection would trigger so Model/Body Style/Doors catch up.
    if (this.makeSelect.value) {
      this.onMakeChange();
    }
  }

  VehicleFinder.prototype.bindEvents = function () {
    this.makeSelect.addEventListener('change', this.onMakeChange.bind(this));
    this.modelSelect.addEventListener('change', this.onModelChange.bind(this));
    this.bodySelect.addEventListener('change', this.onBodyChange.bind(this));
    this.doorsSelect.addEventListener('change', this.onDoorsChange.bind(this));
    this.form.addEventListener('submit', this.onSearch.bind(this));
  };

  VehicleFinder.prototype.setStatus = function (msg) {
    if (this.statusEl) this.statusEl.textContent = msg || '';
  };

  VehicleFinder.prototype.resetSelect = function (select, placeholder) {
    select.innerHTML = '';
    var opt = document.createElement('option');
    opt.value = '';
    opt.textContent = placeholder;
    select.appendChild(opt);
    select.disabled = true;
    delete select.dataset.paramName;
  };

  // Find a filter object from the API response by its display label.
  VehicleFinder.prototype.findFilter = function (filters, label) {
    if (!filters || !label) return null;
    var target = label.trim().toLowerCase();
    for (var i = 0; i < filters.length; i++) {
      if ((filters[i].label || '').trim().toLowerCase() === target) return filters[i];
    }
    return null;
  };

  VehicleFinder.prototype.populateSelect = function (select, filters, label, emptyMessage) {
    var filterObj = this.findFilter(filters, label);
    select.innerHTML = '';

    var placeholderOpt = document.createElement('option');
    placeholderOpt.value = '';
    placeholderOpt.textContent = filterObj ? 'Select ' + label.toLowerCase() : emptyMessage;
    select.appendChild(placeholderOpt);

    if (!filterObj || !filterObj.values || !filterObj.values.length) {
      select.disabled = true;
      return false;
    }

    select.dataset.paramName = filterObj.param_name;

    filterObj.values.forEach(function (value) {
      var opt = document.createElement('option');
      opt.value = value.value; // raw value — must match Shopify's tag/metafield exactly
      var displayLabel = capitalizeWords(value.label);
      opt.textContent = value.count > 0 ? displayLabel + ' (' + value.count + ')' : displayLabel;
      opt.disabled = value.count === 0;
      select.appendChild(opt);
    });

    select.disabled = false;
    return true;
  };

  // Fetch the filters JSON for the current make, with any already-chosen
  // filters applied so Shopify returns correctly recalculated counts.
  VehicleFinder.prototype.fetchFilters = function (activeParams) {
    var token = ++this.requestToken;
    var url = new URL(this.collectionUrl, window.location.origin);
    url.searchParams.set('view', 'filters-api');
    Object.keys(activeParams).forEach(function (key) {
      if (activeParams[key]) url.searchParams.set(key, activeParams[key]);
    });

    this.setStatus('Loading options…');

    return fetch(url.toString(), { headers: { 'X-Requested-With': 'XMLHttpRequest' } })
      .then(function (res) {
        if (!res.ok) throw new Error('Filter request failed: ' + res.status);
        return res.json();
      })
      .then(function (data) {
        if (token !== this.requestToken) return null; // a newer request superseded this one
        this.setStatus('');
        return data;
      }.bind(this))
      .catch(function (err) {
        if (token === this.requestToken) this.setStatus('Something went wrong loading options.');
        console.error('[vehicle-finder]', err);
        return null;
      }.bind(this));
  };

  VehicleFinder.prototype.onMakeChange = function () {
    this.collectionUrl = this.makeSelect.value || null;
    this.selected = { model: null, body: null, doors: null };
    this.resetSelect(this.modelSelect, 'Select make first');
    this.resetSelect(this.bodySelect, 'Select model first');
    this.resetSelect(this.doorsSelect, 'Select body style first');
    this.searchBtn.disabled = true;

    if (!this.collectionUrl) return;

    this.fetchFilters({}).then(function (filters) {
      if (!filters) return;
      this.populateSelect(this.modelSelect, filters, this.labels.modelLabel, 'No models found');
    }.bind(this));
  };

  VehicleFinder.prototype.onModelChange = function () {
    this.selected.model = this.modelSelect.value || null;
    this.selected.body = null;
    this.selected.doors = null;
    this.resetSelect(this.bodySelect, 'Select model first');
    this.resetSelect(this.doorsSelect, 'Select body style first');
    this.searchBtn.disabled = true;

    if (!this.selected.model) return;

    var params = {};
    params[this.modelSelect.dataset.paramName] = this.selected.model;

    this.fetchFilters(params).then(function (filters) {
      if (!filters) return;
      this.populateSelect(this.bodySelect, filters, this.labels.bodyLabel, 'No body styles found');
    }.bind(this));
  };

  VehicleFinder.prototype.onBodyChange = function () {
    this.selected.body = this.bodySelect.value || null;
    this.selected.doors = null;
    this.resetSelect(this.doorsSelect, 'Select body style first');

    // Search becomes available as soon as Make + Model + Body Style are
    // set — Doors is optional from here on.
    this.searchBtn.disabled = !this.selected.body;

    if (!this.selected.body) return;

    var params = {};
    params[this.modelSelect.dataset.paramName] = this.selected.model;
    params[this.bodySelect.dataset.paramName] = this.selected.body;

    this.fetchFilters(params).then(function (filters) {
      if (!filters) return;
      this.populateSelect(this.doorsSelect, filters, this.labels.doorsLabel, 'No options found');
    }.bind(this));
  };

  VehicleFinder.prototype.onDoorsChange = function () {
    // Optional field — just record the value. The search button is
    // already enabled from the Body Style step.
    this.selected.doors = this.doorsSelect.value || null;
  };

  VehicleFinder.prototype.onSearch = function (e) {
    e.preventDefault();
    if (!this.collectionUrl) return;

    var url = new URL(this.collectionUrl, window.location.origin);
    if (this.selected.model && this.modelSelect.dataset.paramName) {
      url.searchParams.set(this.modelSelect.dataset.paramName, this.selected.model);
    }
    if (this.selected.body && this.bodySelect.dataset.paramName) {
      url.searchParams.set(this.bodySelect.dataset.paramName, this.selected.body);
    }
    if (this.selected.doors && this.doorsSelect.dataset.paramName) {
      url.searchParams.set(this.doorsSelect.dataset.paramName, this.selected.doors);
    }
    window.location.href = url.toString();
  };

  window.VehicleFinder = VehicleFinder;
})();